/**
 * My Dashboard — personal hub for the logged-in user.
 * Tabs: Profile · Tasks · Timesheet (coming soon) · Compliance · Skills
 *
 * Profile sources data from /api/v1/me/dashboard/profile (joined user + employee record).
 * Tasks is the unified inbox /me/dashboard/tasks (project / direct / action_item / approval).
 * Skills uses /me/dashboard/skills + /me/skills CRUD.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import { fmtWorkspaceTime } from "@/lib/wsTime";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import {
  UserSquare2, ListChecks, Clock, ShieldCheck, Sparkles,
  Plus, Edit3, Trash2, Calendar as CalendarIcon, Mail, Phone, Building2, IdCard, MapPin, Star, AlertCircle, FolderKanban, ClipboardList, Briefcase, Search, Target, TrendingUp, Video, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { GoalDetailDialog } from "@/pages/app/Goals";
import MyDashboardSummary from "@/components/dashboard/MyDashboardSummary";
import PIPStatusCard from "@/components/PIPStatusCard";
import MyPerfRollupCard from "@/components/MyPerfRollupCard";
import MyFeedbackTile from "@/components/MyFeedbackTile";
import JobDutiesPage from "@/pages/app/hr/JobDutiesPage";  // Iter 252
import { MyDayTab } from "@/pages/app/Timesheet";
import { LeavesTab as AttLeavesTab, MonthTab as AttMonthTab, WfhTab } from "@/pages/app/Attendance";
import { collectPunchMeta } from "@/lib/punchMeta";
import StalePunchDialog from "@/components/attendance/StalePunchDialog";
import { LogIn, LogOut, CheckCircle2, Plane, Home } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const TABS = [
  { value: "dashboard", label: "Dashboard", icon: TrendingUp, perm: "my_dashboard.tab.dashboard.view" },
  { value: "profile", label: "My profile", icon: UserSquare2, perm: "my_dashboard.tab.profile.view" },
  { value: "tasks", label: "My tasks", icon: ListChecks, perm: "my_dashboard.tab.tasks.view" },
  { value: "goals", label: "My goals", icon: Target, perm: "my_dashboard.tab.goals.view" },
  { value: "performance", label: "My performance", icon: TrendingUp, perm: "my_dashboard.tab.performance.view" },
  { value: "job-duties", label: "My job duties", icon: ClipboardList, perm: "my_dashboard.tab.job_duties.view" },  // Iter 252
  { value: "timesheet", label: "My timesheet", icon: Clock, perm: "my_dashboard.tab.timesheet.view" },
  { value: "leave-attendance", label: "My leave & attendance", icon: Plane, perm: "my_dashboard.tab.leave_attendance.view" },
  { value: "compliance", label: "My compliance", icon: ShieldCheck, perm: "my_dashboard.tab.compliance.view" },
  { value: "skills", label: "My skills", icon: Sparkles, perm: "my_dashboard.tab.skills.view" },
];

// R138n · Opt-in restriction: if a role has NONE of the my_dashboard.tab.*
// codes, every subtab remains visible (backward-compatible default). As soon
// as an admin grants ONE, only the granted subtabs are shown. Admin-tier
// roles always see everything.
function useVisibleMyDashboardTabs() {
  const { hasPerm, isAdminRole, ready } = useWorkspacePerms();
  return useMemo(() => {
    if (!ready) return TABS; // fail-open while loading — matches other pages
    if (isAdminRole) return TABS;
    const anyExplicit = TABS.some((t) => hasPerm(t.perm));
    if (!anyExplicit) return TABS;
    return TABS.filter((t) => hasPerm(t.perm));
  }, [hasPerm, isAdminRole, ready]);
}

export default function MyDashboard() {
  const { user, activeWorkspaceId } = useAuth();
  const visibleTabs = useVisibleMyDashboardTabs();
  const visibleValues = useMemo(() => new Set(visibleTabs.map((t) => t.value)), [visibleTabs]);
  const [sp, setSp] = useSearchParams();
  const initialTab = sp.get("tab") || "dashboard";
  // R123 · Redirect legacy ?tab=leaves | ?tab=attendance → combined tab.
  const normalizedInitial = (initialTab === "leaves" || initialTab === "attendance")
    ? "leave-attendance" : initialTab;
  const [tab, setTab] = useState(normalizedInitial);

  useEffect(() => {
    setSp((prev) => { const next = new URLSearchParams(prev); next.set("tab", tab); return next; }, { replace: true });
  }, [tab, setSp]);

  // R138n · If the currently active tab has been permission-restricted away,
  // fall back to the first tab the user CAN see. Prevents landing on an
  // empty view after an admin toggles perms.
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleValues.has(tab)) setTab(visibleTabs[0].value);
  }, [visibleValues, visibleTabs, tab]);

  return (
    <div className="space-y-6" data-testid="my-dashboard-page">
      <PageHeader
        title="My dashboard"
        subtitle="Your personal hub — profile, tasks, compliance, skills."
      />

      <TodayMeetingsStrip wsId={activeWorkspaceId} />
      <MyTaskCountPills wsId={activeWorkspaceId} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          {visibleTabs.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none px-4 py-2"
              data-testid={`my-tab-${t.value}`}
            >
              <t.icon className="w-4 h-4 mr-2" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleValues.has("dashboard") && (
        <TabsContent value="dashboard" className="mt-6 space-y-4">
          <MyDashboardSummary onNavigate={setTab} />
          <MyFeedbackTile />
        </TabsContent>
        )}
        {visibleValues.has("profile") && (
        <TabsContent value="profile" className="mt-6"><ProfileTab wsId={activeWorkspaceId} /></TabsContent>
        )}
        {visibleValues.has("tasks") && (
        <TabsContent value="tasks" className="mt-6"><TasksTab wsId={activeWorkspaceId} userId={user?.id} /></TabsContent>
        )}
        {visibleValues.has("goals") && (
        <TabsContent value="goals" className="mt-6"><MyGoalsTab wsId={activeWorkspaceId} /></TabsContent>
        )}
        {visibleValues.has("performance") && (
        <TabsContent value="performance" className="mt-6 space-y-4" data-testid="my-performance-tab-content">
          <MyPerfRollupCard />
          <PIPStatusCard />
          <MyFeedbackTile />
        </TabsContent>
        )}
        {visibleValues.has("job-duties") && (
        <TabsContent value="job-duties" className="mt-6" data-testid="my-job-duties-tab-content">
          <JobDutiesPage selfMode />
        </TabsContent>
        )}
        {visibleValues.has("timesheet") && (
        <TabsContent value="timesheet" className="mt-6" data-testid="my-timesheet-tab-content"><MyDayTab wsId={activeWorkspaceId} readonly /></TabsContent>
        )}
        {visibleValues.has("leave-attendance") && (
        <TabsContent value="leave-attendance" className="mt-6" data-testid="my-leave-attendance-tab-content">
          {activeWorkspaceId ? <LeaveAttendanceTab wsId={activeWorkspaceId} /> : null}
        </TabsContent>
        )}
        {visibleValues.has("compliance") && (
        <TabsContent value="compliance" className="mt-6"><ComplianceTab wsId={activeWorkspaceId} /></TabsContent>
        )}
        {visibleValues.has("skills") && (
        <TabsContent value="skills" className="mt-6"><SkillsTab wsId={activeWorkspaceId} /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ───────────────────────── R91 · My Attendance tab ─────────────────────────

/**
 * Combines a prominent Check-In / Check-Out control, today-at-a-glance stats
 * (work time, late minutes, monthly totals), and the shared MonthTab which
 * already handles the raw grid + regularization requests.
 */
function MyAttendanceTab({ wsId }) {
  const [today, setToday] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stalePunch, setStalePunch] = useState(null); // R148c · dialog trigger
  const [pendingMeta, setPendingMeta] = useState(null);
  const month = new Date().toISOString().slice(0, 7);

  const loadToday = () => api.get(`/workspaces/${wsId}/attendance/today`).then((r) => setToday(r.data)).catch(() => {});
  const loadSummary = () => api.get(`/workspaces/${wsId}/attendance?month=${month}`).then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { if (wsId) { loadToday(); loadSummary(); } }, [wsId]);   // eslint-disable-line

  const punch = async () => {
    const rec = today?.record;
    const openPunch = today?.open_punch || (rec?.clock_in && !rec?.clock_out ? {
      id: rec.id, clock_in: rec.clock_in, clock_out: null,
    } : null);
    const action = openPunch ? "clock-out" : "clock-in";
    // Iter · Feb 2026 — Confirm before checking out so a mis-click on the
    // big red button doesn't accidentally close the day's session.
    if (action === "clock-out") {
      const ok = window.confirm("Do you want to check out?");
      if (!ok) return;
    }
    setBusy(true);
    try {
      const meta = await collectPunchMeta({ requireLocation: action === "clock-in" });
      // R148b · Multi-punch aware. Server returns 409 with open_punch info
      // when we try to clock in while a previous session is still open —
      // that shouldn't normally happen because we route to clock-out first,
      // but if the tab was left open across a stale-open state we handle it.
      try {
        const { data } = await api.post(`/workspaces/${wsId}/attendance/${action}`, meta);
        toast.success(action === "clock-in" ? "Clocked in — have a great day!" : `Clocked out — ${(data.work_minutes / 60).toFixed(1)}h today`);
      } catch (err) {
        // R148 · Location enforcement WiFi retry.
        if (action === "clock-in" && err?.response?.data?.detail === "Unauthorized check in location") {
          const ssid = window.prompt("Unauthorized check-in location. If you're on an office WiFi, enter its SSID (case-insensitive) to retry:", "");
          if (ssid && ssid.trim()) {
            meta.wifi_ssid = ssid.trim();
            const { data } = await api.post(`/workspaces/${wsId}/attendance/${action}`, meta);
            toast.success(`Clocked in — have a great day!${data.work_minutes ? ` ${(data.work_minutes / 60).toFixed(1)}h` : ""}`);
          } else { throw err; }
        }
        // R148b · Prompt to close a stale open punch, then retry clock-in.
        else if (action === "clock-in" && err?.response?.status === 409 && err?.response?.data?.detail?.code === "open_punch_exists") {
          setStalePunch(err.response.data.detail.open_punch);
          setPendingMeta(meta);
          return; // dialog will complete the flow
        } else { throw err; }
      }
      loadToday();
      loadSummary();
      window.dispatchEvent(new Event("acq-refresh-compliance"));
    } catch (e) {
      if (e?.message && !e.response) toast.error(e.message);
      else toast.error(apiErrorMessage(e, "Punch failed"));
    } finally { setBusy(false); }
  };

  const clockedIn = !!today?.open_punch;
  const punches = today?.record?.punches || [];
  const hasSessionsToday = punches.length > 0;
  const workHrs = ((today?.record?.work_minutes || 0) / 60).toFixed(1);
  const late = today?.record?.late_minutes || 0;
  const openPunchStart = today?.open_punch?.clock_in;

  // Weekly average = sum of work_minutes over last 7 days ÷ punch days.
  const [weeklyAvg, weeklyDays] = useMemo(() => {
    if (!summary?.days) return [0, 0];
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
    const rows = (summary.days || []).filter((d) => d.clock_in && new Date(d.date) >= cutoff);
    if (!rows.length) return [0, 0];
    const sum = rows.reduce((s, d) => s + (d.work_minutes || 0), 0);
    return [(sum / rows.length / 60).toFixed(1), rows.length];
  }, [summary]);
  const monthTotal = useMemo(() => (summary?.days || []).reduce((s, d) => s + (d.work_minutes || 0), 0), [summary]);
  const monthLate = useMemo(() => (summary?.days || []).filter((d) => (d.late_minutes || 0) > 0).length, [summary]);
  const monthPresent = useMemo(() => (summary?.days || []).filter((d) => d.status === "present" || d.status === "half_day").length, [summary]);

  return (
    <div className="space-y-5" data-testid="my-attendance-tab">
      {/* Today · Big Clock In/Out */}
      <Card className="rounded-none">
        <CardContent className="p-5">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex-1 min-w-56">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Today</div>
              <div className="text-2xl font-bold mt-1">
                {clockedIn ? "Clocked in" : hasSessionsToday ? "Between sessions" : "Not clocked in yet"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {openPunchStart && <span className="font-mono">In {fmtWorkspaceTime(openPunchStart)}</span>}
                {today?.record?.work_minutes ? <span className="ml-3">· {workHrs}h worked today</span> : null}
                {late > 0 && <span className="ml-3 text-red-500 font-semibold">· Late {late}m</span>}
              </div>
            </div>
            <Button
              size="lg"
              disabled={busy}
              onClick={punch}
              className={`rounded-none h-14 px-8 text-base font-bold ${
                clockedIn ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
              } text-white`}
              data-testid="my-att-punch-btn"
            >
              {clockedIn ? <><LogOut className="w-5 h-5 mr-2" /> Check out</>
                : hasSessionsToday ? <><LogIn className="w-5 h-5 mr-2" /> Check in again</>
                : <><LogIn className="w-5 h-5 mr-2" /> Check in</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* R148b · Feb 2026 — Read-only session log for today. Shows every
         clock-in / clock-out pair. Highlights the currently-open session. */}
      {punches.length > 0 && (
        <Card className="rounded-none" data-testid="my-att-session-log">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Today&apos;s sessions ({punches.length})
            </div>
            <div className="space-y-1.5">
              {punches.map((p, i) => {
                const open = !p.clock_out;
                const mins = p.work_minutes || 0;
                return (
                  <div
                    key={p.id || i}
                    className={`flex items-center gap-3 text-xs border-l-2 pl-3 py-1 ${open ? "border-emerald-500 bg-emerald-50/40" : "border-muted-foreground/30"}`}
                    data-testid={`att-punch-row-${i}`}
                  >
                    <span className="font-mono">{fmtWorkspaceTime(p.clock_in)}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-mono">{p.clock_out ? fmtWorkspaceTime(p.clock_out) : <span className="text-emerald-700 font-semibold">running…</span>}</span>
                    <span className="ml-auto text-muted-foreground">
                      {open ? "" : `${(mins / 60).toFixed(2)}h`}
                    </span>
                    {p.needs_review && <span className="text-[10px] text-amber-700 border border-amber-500 px-1 ml-1">needs review</span>}
                    {p.closed_by === "auto_12h_cap" && <span className="text-[10px] text-red-700 border border-red-500 px-1 ml-1">auto-closed (12h cap)</span>}
                    {p.closed_by === "nightly_autoclose" && <span className="text-[10px] text-red-700 border border-red-500 px-1 ml-1">nightly auto-closed</span>}
                    {p.closed_by === "user_prompt" && <span className="text-[10px] text-blue-700 border border-blue-500 px-1 ml-1">closed later</span>}
                  </div>
                );
              })}
            </div>
            {today?.stale_open_from_prev_day && (
              <div className="mt-2 text-[11px] text-amber-700 border border-amber-500 bg-amber-50 p-2">
                A session from yesterday is still open. Your next check-in will prompt you to enter the actual clock-out time.
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTileMini label="Present days · this month" value={monthPresent} testid="att-tile-present" />
        <SummaryTileMini label="Monthly hours" value={`${(monthTotal / 60).toFixed(1)}h`} testid="att-tile-monthly" />
        <SummaryTileMini label="Weekly avg · last 7 days" value={`${weeklyAvg}h`} sub={`${weeklyDays} days`} testid="att-tile-weekly" />
        <SummaryTileMini
          label="Late days · this month"
          value={monthLate}
          testid="att-tile-late"
          tone={monthLate > 0 ? "amber" : ""}
        />
      </div>

      {/* Late trend micro-strip */}
      {summary?.days?.length ? (
        <div className="border border-border p-3" data-testid="att-late-trend">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Last-30-days late-minute trend</div>
          <div className="flex items-end gap-0.5 h-10">
            {(summary.days || []).slice(-30).map((d) => {
              const h = Math.min(38, d.late_minutes || 0);
              const cls = (d.late_minutes || 0) > 15 ? "bg-red-500" : (d.late_minutes || 0) > 0 ? "bg-amber-500" : "bg-emerald-500/40";
              return <div key={d.date} title={`${d.date}: ${d.late_minutes || 0}m late`} className={`w-2 ${cls}`} style={{ height: Math.max(2, h) }} />;
            })}
          </div>
        </div>
      ) : null}

      {/* Full grid + regularization requests */}
      <div className="border border-border p-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Attendance records</div>
        <AttMonthTab wsId={wsId} />
      </div>

      {stalePunch && (
        <StalePunchDialog
          openPunch={stalePunch}
          onCancel={() => { setStalePunch(null); setPendingMeta(null); }}
          onConfirm={async (isoOut) => {
            try {
              await api.post(`/workspaces/${wsId}/attendance/close-open-punch`, {
                punch_id: stalePunch.id, clock_out_at: isoOut,
              });
              await api.post(`/workspaces/${wsId}/attendance/clock-in`, pendingMeta || {});
              toast.success("Previous session closed. New session started — have a great day!");
              setStalePunch(null);
              setPendingMeta(null);
              loadToday();
              loadSummary();
              window.dispatchEvent(new Event("acq-refresh-compliance"));
            } catch (e) {
              throw e;
            }
          }}
        />
      )}
    </div>
  );
}

// R148c · Feb 2026 — extracted so we can render the StalePunchDialog
// inside the tab without changing the outer structure.
function SummaryTileMini({ label, value, sub, testid, tone = "" }) {
  const cls = tone === "amber" ? "border-amber-400/60 bg-amber-50" : "border-border";
  return (
    <Card className={`rounded-none ${cls}`}>
      <CardContent className="p-3">
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <div className="text-2xl font-bold mt-1" data-testid={testid}>{value}</div>
        {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}


// ─── R123 · Combined "My Leave & Attendance" tab (Feb 2026) ────────────
//
// Top strip: summary tiles + a 30-day late-minute sparkline shared
// across all three subtabs so users always have context regardless of
// which subtab they're on.
// Subtabs: My Attendance (default) · My Leaves · My WFH.
//
// Sources data from `/workspaces/{ws}/attendance?month=…` +
// `/workspaces/{ws}/leaves?mine=true` for the summary bar.

function LeaveAttendanceTab({ wsId }) {
  const [summary, setSummary] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [subtab, setSubtab] = useState("attendance");
  const month = new Date().toISOString().slice(0, 7);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/attendance?month=${month}`)
       .then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/workspaces/${wsId}/leaves?mine=true`)
       .then((r) => setLeaves(r.data || [])).catch(() => setLeaves([]));
  }, [wsId, month]);

  const monthTotalMin = useMemo(() =>
    (summary?.days || []).reduce((s, d) => s + (d.work_minutes || 0), 0),
  [summary]);
  const monthPresent = useMemo(() =>
    (summary?.days || []).filter((d) => d.status === "present" || d.status === "half_day").length,
  [summary]);
  const monthLate = useMemo(() =>
    (summary?.days || []).filter((d) => (d.late_minutes || 0) > 0).length,
  [summary]);
  const leavesTakenYtd = useMemo(() =>
    (leaves || []).filter((l) =>
      l.status === "approved" && (l.start_date || "").startsWith(String(currentYear))
    ).reduce((s, l) => s + (Number(l.days) || 1), 0),
  [leaves, currentYear]);
  const leavesPending = useMemo(() =>
    (leaves || []).filter((l) => l.status === "pending").length,
  [leaves]);

  return (
    <div className="space-y-5" data-testid="leave-attendance-tab">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTileMini label="Present days · this month" value={monthPresent}
                          testid="la-tile-present" />
        <SummaryTileMini label="Monthly hours"
                          value={`${(monthTotalMin / 60).toFixed(1)}h`}
                          testid="la-tile-monthly" />
        <SummaryTileMini label="Late days · this month" value={monthLate}
                          testid="la-tile-late"
                          tone={monthLate > 0 ? "amber" : ""} />
        <SummaryTileMini label="Leaves taken · YTD" value={leavesTakenYtd}
                          sub={leavesPending > 0 ? `${leavesPending} pending` : null}
                          testid="la-tile-leaves-ytd" />
      </div>

      {/* Late-minute trend sparkline (last 30 days) */}
      {summary?.days?.length ? (
        <div className="border border-border p-3" data-testid="la-late-trend">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            Last-30-days late-minute trend
          </div>
          <div className="flex items-end gap-0.5 h-10">
            {(summary.days || []).slice(-30).map((d) => {
              const h = Math.min(38, d.late_minutes || 0);
              const cls = (d.late_minutes || 0) > 15 ? "bg-red-500"
                          : (d.late_minutes || 0) > 0 ? "bg-amber-500"
                          : "bg-emerald-500/40";
              return <div key={d.date} title={`${d.date}: ${d.late_minutes || 0}m late`}
                          className={`w-2 ${cls}`}
                          style={{ height: Math.max(2, h) }} />;
            })}
          </div>
        </div>
      ) : null}

      {/* Subtabs */}
      <Tabs value={subtab} onValueChange={setSubtab}>
        <TabsList className="rounded-none">
          <TabsTrigger value="attendance" className="rounded-none" data-testid="la-subtab-attendance">
            <CalendarIcon className="w-4 h-4 mr-2" />My Attendance
          </TabsTrigger>
          <TabsTrigger value="leaves" className="rounded-none" data-testid="la-subtab-leaves">
            <Plane className="w-4 h-4 mr-2" />My Leaves
          </TabsTrigger>
          <TabsTrigger value="wfh" className="rounded-none" data-testid="la-subtab-wfh">
            <Home className="w-4 h-4 mr-2" />My WFH
          </TabsTrigger>
        </TabsList>
        <TabsContent value="attendance" className="mt-4" data-testid="la-attendance-content">
          <MyAttendanceTab wsId={wsId} />
        </TabsContent>
        <TabsContent value="leaves" className="mt-4" data-testid="la-leaves-content">
          <AttLeavesTab wsId={wsId} />
        </TabsContent>
        <TabsContent value="wfh" className="mt-4" data-testid="la-wfh-content">
          <WfhTab wsId={wsId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ───────────────────────── Today's meetings ─────────────────────────

function TodayMeetingsStrip({ wsId }) {
  const [meetings, setMeetings] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/upcoming-events?days=1`)
      .then((r) => {
        const today = new Date().toISOString().slice(0, 10);
        setMeetings((r.data || []).filter((e) => (e.start_at || "").slice(0, 10) === today));
      })
      .catch(() => setMeetings([]));
  }, [wsId]);

  if (meetings.length === 0) return null;
  return (
    <div className="border border-border bg-muted/30" data-testid="today-meetings-strip">
      <div className="px-3 py-1.5 border-b border-border flex items-center gap-2">
        <CalendarIcon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-widest font-semibold">Today&apos;s meetings · {meetings.length}</span>
        <Link to="/app/calendar" className="text-[10px] text-primary hover:underline ml-auto" data-testid="today-meetings-open-calendar">Open calendar →</Link>
      </div>
      <div className="flex gap-2 overflow-x-auto p-2">
        {meetings.map((e) => (
          <div key={e.occurrence_id || e.id} className="shrink-0 border border-border bg-background px-3 py-2 flex items-center gap-3 min-w-[220px]" data-testid={`today-meeting-${e.id}`}>
            <span className="w-1.5 self-stretch" style={{ backgroundColor: e.calendar_color || "#7c3aed" }} />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                {e.recurrence?.freq && <Repeat className="w-3 h-3 shrink-0 text-muted-foreground" />}
                {e.title}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span className="font-mono">{(e.start_at || "").slice(11, 16)}</span>
                {e.location && <span className="truncate max-w-[120px] inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{e.location}</span>}
              </div>
            </div>
            {e.meeting_link && (
              <a href={e.meeting_link} target="_blank" rel="noreferrer"
                className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-primary text-primary-foreground hover:opacity-90"
                data-testid={`today-meeting-join-${e.id}`}>
                <Video className="w-3 h-3" /> Join
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────── Profile ─────────────────────────

function ProfileTab({ wsId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/me/dashboard/profile?ws_id=${wsId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load profile"));
  }, [wsId]);

  if (err) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-destructive" data-testid="profile-error">{err}</CardContent></Card>;
  if (!data) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;

  const { user: u, employee: emp, department_name, manager_name, is_employee } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="profile-tab">
      {/* Identity */}
      <Card className="rounded-none lg:col-span-1">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold rounded-none">
              {(u?.name || u?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold leading-tight" data-testid="profile-name">{u?.name || "Unnamed"}</h3>
              <p className="text-xs text-foreground/60 mt-0.5 break-all" data-testid="profile-email">{u?.email}</p>
              {is_employee ? (
                <Badge variant="outline" className="mt-3 rounded-none" data-testid="profile-employee-badge">Employee</Badge>
              ) : (
                <Badge variant="outline" className="mt-3 rounded-none border-dashed">Workspace member</Badge>
              )}
            </div>
          </div>

          <div className="mt-5 space-y-2 text-sm">
            <Row icon={Briefcase} label="Designation" value={emp?.designation || "—"} testid="profile-designation" />
            <Row icon={Building2} label="Department" value={department_name || "—"} testid="profile-department" />
            <Row icon={IdCard} label="Employment" value={emp?.employment_type || "—"} testid="profile-employment-type" />
            <Row icon={IdCard} label="Employee code" value={emp?.employee_code || "—"} testid="profile-emp-code" />
            <Row icon={UserSquare2} label="Reports to" value={manager_name || "—"} testid="profile-reports-to" />
            <Row icon={CalendarIcon} label="Joined on" value={emp?.date_of_joining || "—"} testid="profile-doj" />
          </div>
        </CardContent>
      </Card>

      {/* Contact & Personal */}
      <Card className="rounded-none lg:col-span-2">
        <CardContent className="p-5">
          <h4 className="label-eyebrow mb-4">Contact & personal</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Field icon={Mail} label="Email" value={u?.email} testid="profile-field-email" />
            <Field icon={Phone} label="Phone" value={emp?.phone || u?.phone || "—"} testid="profile-field-phone" />
            <Field icon={CalendarIcon} label="Date of birth" value={emp?.date_of_birth || "—"} testid="profile-field-dob" />
            <Field label="Gender" value={emp?.gender || "—"} testid="profile-field-gender" />
            <Field label="Marital status" value={emp?.marital_status || "—"} testid="profile-field-marital" />
            <Field label="Nationality" value={emp?.nationality || "—"} testid="profile-field-nationality" />
            <Field icon={MapPin} label="Current address" value={emp?.current_address || "—"} testid="profile-field-address" className="md:col-span-2" />
            <Field icon={MapPin} label="Permanent address" value={emp?.permanent_address || "—"} testid="profile-field-perm-address" className="md:col-span-2" />
          </div>

          <div className="border-t border-border mt-6 pt-5">
            <h4 className="label-eyebrow mb-4">Emergency contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Name" value={emp?.emergency_contact_name || "—"} testid="profile-em-name" />
              <Field label="Relationship" value={emp?.emergency_contact_relation || "—"} testid="profile-em-relation" />
              <Field icon={Phone} label="Phone" value={emp?.emergency_contact_phone || "—"} testid="profile-em-phone" />
              <Field icon={Mail} label="Email" value={emp?.emergency_contact_email || "—"} testid="profile-em-email" />
            </div>
          </div>

          <div className="border-t border-border mt-6 pt-5">
            <h4 className="label-eyebrow mb-4">Bank details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Account holder" value={emp?.bank_account_holder || "—"} testid="profile-bank-holder" />
              <Field label="Bank name" value={emp?.bank_name || "—"} testid="profile-bank-name" />
              <Field label="Account number" value={emp?.bank_account_number ? `••••${String(emp.bank_account_number).slice(-4)}` : "—"} testid="profile-bank-acct" />
              <Field label="IFSC / Routing" value={emp?.bank_ifsc || "—"} testid="profile-bank-ifsc" />
            </div>
          </div>

          {emp && (
            <div className="border-t border-border mt-6 pt-5 flex items-center justify-between text-xs text-foreground/60">
              <span>Profile data is read-only here. Updates flow through HR.</span>
              <Link to={`/app/employees/${emp.id}`} className="underline hover:no-underline" data-testid="profile-open-hrms">Open HRMS record →</Link>
            </div>
          )}
          {!is_employee && (
            <p className="text-xs text-foreground/60 mt-6" data-testid="profile-no-employee-hint">
              No HRMS employee record is linked to your account. Personal information will appear here once HR creates your record.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2 text-foreground/80" data-testid={testid}>
      {Icon && <Icon className="w-3.5 h-3.5 text-foreground/50 shrink-0" />}
      <span className="text-xs text-foreground/55 w-28 shrink-0">{label}</span>
      <span className="font-medium truncate">{value}</span>
    </div>
  );
}

function Field({ icon: Icon, label, value, testid, className = "" }) {
  return (
    <div className={className} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-foreground/55">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </div>
      <div className="mt-0.5 font-medium break-words">{value ?? "—"}</div>
    </div>
  );
}

// ───────────────────────── Tasks (unified inbox) ─────────────────────────

const SOURCE_META = {
  project: { label: "Project", icon: FolderKanban },
  action_item: { label: "Action item", icon: ClipboardList },
  approval: { label: "Approval", icon: CheckCircle2 },
  compliance: { label: "Compliance", icon: ShieldCheck },
  direct: { label: "Direct", icon: ListChecks },
  other: { label: "Other", icon: ListChecks },
};

function TasksTab({ wsId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [includeDone, setIncludeDone] = useState(false);
  const reload = () => {
    if (!wsId) return;
    api.get(`/me/dashboard/tasks?ws_id=${wsId}&include_done=${includeDone}`).then((r) => setData(r.data));
  };
  useEffect(() => { reload(); }, [wsId, includeDone]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.items;
    return data.items.filter((it) => it.source === filter);
  }, [data, filter]);

  if (!data) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;

  return (
    <div className="space-y-4" data-testid="tasks-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          <SourceChip active={filter === "all"} onClick={() => setFilter("all")} label="All" count={data.summary.total} testid="tasks-filter-all" />
          {Object.entries(data.summary.by_source).map(([src, n]) => {
            const meta = SOURCE_META[src] || SOURCE_META.other;
            return <SourceChip key={src} active={filter === src} onClick={() => setFilter(src)} icon={meta.icon} label={meta.label} count={n} testid={`tasks-filter-${src}`} />;
          })}
        </div>
        <label className="text-xs flex items-center gap-2 cursor-pointer" data-testid="tasks-include-done">
          <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} className="rounded-none" />
          Include completed
        </label>
      </div>

      {filtered.length === 0 ? (
        <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-foreground/60 text-sm" data-testid="tasks-empty">Nothing on your plate from this source. <span className="text-foreground/40">Enjoy the quiet.</span></CardContent></Card>
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="tasks-list">
          {filtered.map((it) => <TaskRow key={it.id} item={it} />)}
        </div>
      )}
    </div>
  );
}

function SourceChip({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 border ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-foreground/5"} flex items-center gap-1.5 rounded-none`}
      data-testid={testid}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
      <span className={`text-[10px] ${active ? "text-primary-foreground/80" : "text-foreground/50"}`}>{count}</span>
    </button>
  );
}

function TaskRow({ item }) {
  const meta = SOURCE_META[item.source] || SOURCE_META.other;
  const Icon = meta.icon;
  const priorityColor = {
    urgent: "border-red-500/60 text-red-600 bg-red-50 dark:bg-red-950",
    high: "border-amber-500/60 text-amber-600",
    medium: "border-foreground/30",
    low: "border-foreground/20 text-foreground/60",
  }[item.priority || "medium"] || "";
  const link = item._inbox_kind === "skill_approval" ? "/app/skill-center?tab=approvals" : `/app/tasks?focus=${item.id}`;
  return (
    <Link to={link} className="block px-4 py-3 hover:bg-foreground/5 transition-colors" data-testid={`task-row-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 border border-border flex items-center justify-center shrink-0 text-foreground/60">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{item.title}</span>
            {item.priority && (
              <span className={`text-[10px] uppercase tracking-wide border px-1.5 py-0.5 ${priorityColor}`}>{item.priority}</span>
            )}
            {item._inbox_kind === "skill_approval" && item.owner_name && (
              <span className="text-[11px] text-foreground/60">by {item.owner_name}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-foreground/50 mt-0.5">
            <span>{meta.label}</span>
            {item.due_date && <span>· due {item.due_date}</span>}
            {item.status && item._inbox_kind === "task" && <span>· {item.status}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

// ───────────────────────── Compliance ─────────────────────────

function ComplianceTab({ wsId }) {
  // Wire only what's already available — skill records that are pending or rejected count as my own pending compliance items.
  const [mySkills, setMySkills] = useState(null);
  useEffect(() => { if (!wsId) return; api.get(`/me/dashboard/skills?ws_id=${wsId}`).then((r) => setMySkills(r.data)); }, [wsId]);

  if (!mySkills) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;

  const pending = mySkills.filter((s) => s.status === "pending_approval" || s.status === "edit_pending");
  const rejected = mySkills.filter((s) => s.status === "rejected");
  const approved = mySkills.filter((s) => s.status === "approved");

  return (
    <div className="space-y-4" data-testid="compliance-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Skill records approved" value={approved.length} tone="ok" testid="compliance-stat-approved" />
        <StatCard label="Awaiting manager approval" value={pending.length} tone="warn" testid="compliance-stat-pending" />
        <StatCard label="Action needed" value={rejected.length} tone="danger" testid="compliance-stat-rejected" />
      </div>

      <Card className="rounded-none border-dashed">
        <CardContent className="p-6 text-sm text-foreground/70" data-testid="compliance-info">
          <p className="font-semibold mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> My compliance</p>
          <p>This view will roll up compliance signals from every module — policy acknowledgements, HR documents, training certifications, expense submissions, timesheet sign-offs, IT credentials hygiene and more — as those features are built out.</p>
          <p className="mt-3 text-foreground/60">Today it surfaces only the items already produced by the platform: your <strong>skill record approval status</strong>. New compliance sources will plug in as we develop them.</p>
        </CardContent>
      </Card>

      {(pending.length + rejected.length) > 0 && (
        <Card className="rounded-none">
          <CardContent className="p-5">
            <div className="label-eyebrow mb-3">Open items</div>
            <ul className="text-sm divide-y divide-border" data-testid="compliance-open-list">
              {pending.map((s) => (
                <li key={s.id} className="py-2 flex items-center gap-3" data-testid={`compliance-row-${s.id}`}>
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="flex-1">Skill record <strong>{s.skill_name}</strong> is awaiting manager approval.</span>
                  <Badge variant="outline" className="rounded-none text-[10px]">{s.status === "edit_pending" ? "edit pending" : "pending"}</Badge>
                </li>
              ))}
              {rejected.map((s) => (
                <li key={s.id} className="py-2 flex items-center gap-3" data-testid={`compliance-row-${s.id}`}>
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                  <span className="flex-1">Skill record <strong>{s.skill_name}</strong> was rejected{s.rejected_reason ? `: ${s.rejected_reason}` : ""}.</span>
                  <Badge variant="outline" className="rounded-none text-[10px] border-red-500/40 text-red-600">rejected</Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const tones = {
    ok: "border-emerald-500/30",
    warn: "border-amber-500/40",
    danger: "border-red-500/40",
  };
  return (
    <Card className={`rounded-none ${tones[tone] || ""}`}>
      <CardContent className="p-5" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div>
        <div className="text-3xl font-bold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

// ───────────────────────── Skills ─────────────────────────

function SkillsTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [view, setView] = useState(() => { try { return localStorage.getItem("co_my_skills_view") || "table"; } catch { return "table"; } });
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("skill_name");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => { try { localStorage.setItem("co_my_skills_view", view); } catch { /* ignore */ } }, [view]);

  const reload = () => api.get(`/me/dashboard/skills?ws_id=${wsId}`).then((r) => setRows(r.data));
  useEffect(() => {
    if (!wsId) return;
    reload();
    api.get(`/skill-categories`).then((r) => { setCategories(r.data.categories); setLabels(r.data.labels); });

  }, [wsId]);

  const onDelete = async (s) => {
    if (!window.confirm(`Delete skill "${s.skill_name}"?`)) return;
    await api.delete(`/me/skills/${s.id}`);
    toast.success("Skill removed");
    reload();
  };

  const sorted = useMemo(() => {
    if (!rows) return [];
    const ql = q.trim().toLowerCase();
    let arr = rows;
    if (ql) arr = arr.filter((r) => (r.skill_name + " " + r.category + " " + (r.comments || "")).toLowerCase().includes(ql));
    const dir = sortDir === "asc" ? 1 : -1;
    return [...arr].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, q, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  if (!rows) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;

  return (
    <div className="space-y-4" data-testid="skills-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-foreground/60">{rows.length} skill record{rows.length === 1 ? "" : "s"} · changes go to your manager for approval.</p>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-8 pl-8 text-xs w-44 rounded-none" data-testid="my-skills-search" />
          </div>
          <div className="flex border border-border" data-testid="my-skills-view-toggle">
            <button onClick={() => setView("table")} className={`px-2.5 py-1 text-[11px] ${view === "table" ? "bg-primary text-primary-foreground" : "hover:bg-foreground/5"}`} data-testid="my-skills-view-table">Table</button>
            <button onClick={() => setView("cards")} className={`px-2.5 py-1 text-[11px] ${view === "cards" ? "bg-primary text-primary-foreground" : "hover:bg-foreground/5"}`} data-testid="my-skills-view-cards">Cards</button>
          </div>
          <Button className="rounded-none" onClick={() => setEditing({})} data-testid="my-skills-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Add skill
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="skills-empty">No skills yet — add your first one to build your profile.</CardContent></Card>
      ) : view === "table" ? (
        <div className="border border-border" data-testid="my-skills-table">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-foreground/5 text-[10px] uppercase tracking-widest text-foreground/55 font-semibold">
            <SortHeader testid="sort-name" className="col-span-3" active={sortKey === "skill_name"} dir={sortDir} onClick={() => toggleSort("skill_name")}>Skill</SortHeader>
            <SortHeader testid="sort-category" className="col-span-2" active={sortKey === "category"} dir={sortDir} onClick={() => toggleSort("category")}>Category</SortHeader>
            <SortHeader testid="sort-exp" className="col-span-1" active={sortKey === "total_experience_months"} dir={sortDir} onClick={() => toggleSort("total_experience_months")}>Exp.</SortHeader>
            <SortHeader testid="sort-self" className="col-span-1" active={sortKey === "self_rating"} dir={sortDir} onClick={() => toggleSort("self_rating")}>Self</SortHeader>
            <SortHeader testid="sort-mgr" className="col-span-1" active={sortKey === "manager_rating"} dir={sortDir} onClick={() => toggleSort("manager_rating")}>Mgr</SortHeader>
            <SortHeader testid="sort-last" className="col-span-2" active={sortKey === "last_used_date"} dir={sortDir} onClick={() => toggleSort("last_used_date")}>Last used</SortHeader>
            <SortHeader testid="sort-status" className="col-span-1" active={sortKey === "status"} dir={sortDir} onClick={() => toggleSort("status")}>Status</SortHeader>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {sorted.map((s) => (
            <div key={s.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-border items-center text-sm hover:bg-foreground/5" data-testid={`my-skill-row-${s.id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-medium truncate">{s.skill_name}</div>
                {s.comments && <div className="text-[10px] text-foreground/55 truncate">{s.comments.split("\n")[0]}</div>}
              </div>
              <div className="col-span-2 text-xs truncate">{s.category}</div>
              <div className="col-span-1 text-xs font-mono">{s.total_experience_months}m</div>
              <div className="col-span-1"><RatingStars value={s.self_rating} label="" max={5} /></div>
              <div className="col-span-1">{s.manager_rating ? <RatingStars value={s.manager_rating} label="" max={5} /> : <span className="text-foreground/30 text-xs">—</span>}</div>
              <div className="col-span-2 text-[11px] text-foreground/65">{s.last_used_date || "—"}</div>
              <div className="col-span-1"><StatusBadge status={s.status} /></div>
              <div className="col-span-1 text-right flex justify-end gap-1">
                <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setEditing(s)} data-testid={`my-skill-edit-${s.id}`}><Edit3 className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive hover:text-destructive" onClick={() => onDelete(s)} data-testid={`my-skill-delete-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-skills-list">
          {sorted.map((s) => (
            <Card key={s.id} className="rounded-none" data-testid={`my-skill-card-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-bold leading-tight">{s.skill_name}</h4>
                    <p className="text-[11px] text-foreground/55 mt-0.5">{s.category}</p>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-foreground/70">
                  <RatingStars value={s.self_rating} label="Self" />
                  {s.manager_rating && <RatingStars value={s.manager_rating} label="Manager" />}
                </div>
                <div className="mt-2 text-[11px] text-foreground/55">
                  {s.total_experience_months} month{s.total_experience_months === 1 ? "" : "s"} · {s.self_rating_label}
                  {s.last_used_date && <> · last used {s.last_used_date}</>}
                </div>
                {s.comments && <p className="mt-2 text-[11px] text-foreground/65 whitespace-pre-wrap">{s.comments}</p>}
                <div className="mt-3 flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" className="rounded-none h-7" onClick={() => setEditing(s)} data-testid={`my-skill-edit-card-${s.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 text-destructive hover:text-destructive" onClick={() => onDelete(s)} data-testid={`my-skill-delete-card-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <SkillEditor
          skill={editing}
          wsId={wsId}
          categories={categories}
          labels={labels}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function SortHeader({ children, active, dir, onClick, className, testid }) {
  return (
    <button onClick={onClick} className={`${className} text-left flex items-center gap-1 hover:text-foreground`} data-testid={testid}>
      {children}
      {active && <span className="text-[8px]">{dir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}

export function StatusBadge({ status }) {
  const map = {
    approved: { label: "Approved", cls: "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" },
    pending_approval: { label: "Pending", cls: "border-amber-500/40 text-amber-700 dark:text-amber-400" },
    edit_pending: { label: "Edit pending", cls: "border-amber-500/40 text-amber-700 dark:text-amber-400" },
    rejected: { label: "Rejected", cls: "border-red-500/40 text-red-700 dark:text-red-400" },
  };
  const m = map[status] || { label: status, cls: "" };
  return <Badge variant="outline" className={`rounded-none text-[10px] ${m.cls}`} data-testid={`status-badge-${status}`}>{m.label}</Badge>;
}

export function RatingStars({ value, label, max = 5 }) {
  const v = Number(value) || 0;
  return (
    <span className="inline-flex items-center gap-1" title={`${label}: ${v}/${max}`}>
      <span className="text-[10px] uppercase tracking-wide text-foreground/55">{label}</span>
      <span className="inline-flex">
        {Array.from({ length: max }).map((_, i) => (
          <Star key={i} className={`w-3 h-3 ${i < v ? "text-amber-500 fill-amber-500" : "text-foreground/20"}`} />
        ))}
      </span>
    </span>
  );
}

export function SkillEditor({ skill, wsId, categories, labels, onClose, onSaved, ownerNote }) {
  const isNew = !skill?.id;
  const [form, setForm] = useState(() => ({
    skill_name: skill?.skill_name || "",
    category: skill?.category || (categories[0] || "Technical"),
    last_used_date: skill?.last_used_date || "",
    total_experience_months: skill?.total_experience_months ?? 0,
    self_rating: skill?.self_rating ?? 3,
    self_rating_label: skill?.self_rating_label || "",
    comments: skill?.comments || "",
  }));
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.skill_name.trim()) { toast.error("Skill name is required"); return; }
    setBusy(true);
    try {
      if (isNew) {
        await api.post(`/me/skills`, { ...form, workspace_id: wsId });
        toast.success("Skill added — pending manager approval");
      } else {
        await api.patch(`/me/skills/${skill.id}`, form);
        toast.success(skill.status === "approved" ? "Edit submitted for re-approval" : "Skill updated");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save skill"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="skill-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add skill" : "Edit skill"}</DialogTitle>
        </DialogHeader>
        {ownerNote && <p className="text-xs text-foreground/55 -mt-2 mb-2">{ownerNote}</p>}
        {!isNew && skill?.status === "approved" && (
          <div className="border border-amber-500/30 bg-amber-50 dark:bg-amber-950 p-3 text-xs" data-testid="skill-edit-warn-reapproval">
            Editing an approved skill will <strong>reset it for re-approval</strong>. The current approved version stays visible until your manager approves the edit.
          </div>
        )}
        <div className="space-y-4">
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Skill name</Label>
            <Input value={form.skill_name} onChange={(e) => update("skill_name", e.target.value)} className="h-10 rounded-none" placeholder="e.g. React, Public Speaking, AWS" data-testid="skill-input-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Category</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="skill-select-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(categories.length ? categories : ["Technical", "Soft Skill", "Other"]).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Last used (optional)</Label>
              <Input type="date" value={form.last_used_date || ""} onChange={(e) => update("last_used_date", e.target.value)} className="h-10 rounded-none" data-testid="skill-input-last-used" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Total experience (months)</Label>
              <Input type="number" min={0} max={600} value={form.total_experience_months} onChange={(e) => update("total_experience_months", Math.max(0, parseInt(e.target.value || "0", 10)))} className="h-10 rounded-none" data-testid="skill-input-exp" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Self rating</Label>
              <StarPicker value={form.self_rating} onChange={(v) => update("self_rating", v)} testid="skill-rating-self" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Proficiency label (optional)</Label>
            <Select value={form.self_rating_label || ""} onValueChange={(v) => update("self_rating_label", v === "__none" ? "" : v)}>
              <SelectTrigger className="h-10 rounded-none" data-testid="skill-select-label"><SelectValue placeholder="Auto from stars" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Auto from stars</SelectItem>
                {(labels.length ? labels : ["Beginner", "Intermediate", "Advanced", "Expert"]).map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Comments (optional)</Label>
            <Textarea rows={3} value={form.comments || ""} onChange={(e) => update("comments", e.target.value)} className="rounded-none" placeholder="Context, certifications, notable projects…" data-testid="skill-input-comments" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="skill-editor-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="skill-editor-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StarPicker({ value, onChange, max = 5, testid = "star-picker" }) {
  return (
    <div className="flex items-center h-10 gap-0.5" data-testid={testid}>
      {Array.from({ length: max }).map((_, i) => {
        const v = i + 1;
        const filled = v <= (value || 0);
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`w-7 h-7 flex items-center justify-center hover:scale-110 transition-transform ${filled ? "text-amber-500" : "text-foreground/25"}`}
            data-testid={`${testid}-star-${v}`}
          >
            <Star className={`w-5 h-5 ${filled ? "fill-amber-500" : ""}`} />
          </button>
        );
      })}
      <span className="ml-2 text-xs text-foreground/55">{value || 0}/{max}</span>
    </div>
  );
}


// ───────────────────────── My Goals tab ─────────────────────────

const CONF_STYLE = {
  on_track: "border-emerald-500/40 text-emerald-700",
  at_risk: "border-amber-500/40 text-amber-700",
  off_track: "border-red-500/40 text-red-700",
};
const HORIZON_LABELS_MY = {
  vision: "Vision", long: "Long-term", annual: "Annual", quarterly: "Quarterly", monthly: "Monthly",
};

function MyGoalsTab({ wsId }) {
  const [data, setData] = useState(null);
  const [horizon, setHorizon] = useState("_all");
  const [status, setStatus] = useState("_all");
  const [openGoalId, setOpenGoalId] = useState(null);
  const load = () => {
    if (!wsId) return;
    const p = new URLSearchParams({ ws: wsId });
    if (horizon !== "_all") p.set("horizon", horizon);
    if (status !== "_all") p.set("status", status);
    api.get(`/me/goals?${p.toString()}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, horizon, status]);
  if (!data) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;
  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="my-goals-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat icon={Target} label="Total" value={s.total} testid="mg-total" />
        <MiniStat icon={TrendingUp} label="Active" value={s.active} testid="mg-active" />
        <MiniStat icon={CheckCircle2} label="Completed" value={s.completed} testid="mg-completed" />
        <MiniStat icon={TrendingUp} label="Avg progress" value={`${s.avg_progress_pct}%`} testid="mg-avg" />
        <MiniStat icon={CheckCircle2} label="On track" value={s.on_track} testid="mg-ontrack" />
        <MiniStat icon={AlertCircle} label="At risk" value={s.at_risk} testid="mg-atrisk" />
        <MiniStat icon={AlertCircle} label="Off track" value={s.off_track} testid="mg-offtrack" />
        <MiniStat icon={Clock} label="Overdue check-ins" value={s.overdue_checkins} testid="mg-overdue" />
      </div>
      <div className="flex items-center gap-2">
        <Select value={horizon} onValueChange={setHorizon}>
          <SelectTrigger className="h-9 w-48 rounded-none" data-testid="mg-horizon-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All horizons</SelectItem>
            {["vision", "long", "annual", "quarterly", "monthly"].map((h) => <SelectItem key={h} value={h}>{HORIZON_LABELS_MY[h]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-40 rounded-none" data-testid="mg-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All statuses</SelectItem>
            {["draft", "active", "completed", "cancelled"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Link to="/app/goals" className="text-xs underline hover:no-underline ml-auto" data-testid="mg-open-workspace">Manage in workspace →</Link>
      </div>
      {data.goals.length === 0 ? (
        <Card className="rounded-none border-dashed">
          <CardContent className="p-10 text-center space-y-2" data-testid="mg-empty">
            <Target className="w-10 h-10 mx-auto text-foreground/40" />
            <p className="text-sm text-foreground/60 max-w-md mx-auto">You don&apos;t own or collaborate on any goals yet. Ask your manager to assign one, or open the workspace Goals page to create your own.</p>
            <Link to="/app/goals" className="inline-block text-xs underline hover:no-underline">Go to workspace goals →</Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="mg-goals-list">
          {data.goals.map((g) => (
            <button key={g.id} onClick={() => setOpenGoalId(g.id)} className="w-full text-left border border-border p-4 hover:bg-foreground/5" data-testid={`mg-goal-${g.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className="rounded-none text-[10px] uppercase tracking-widest">{HORIZON_LABELS_MY[g.horizon] || g.horizon}</Badge>
                    <Badge variant="outline" className="rounded-none text-[10px]">{g.status}</Badge>
                    {g.confidence && <Badge variant="outline" className={`rounded-none text-[10px] ${CONF_STYLE[g.confidence] || ""}`}>{g.confidence.replace(/_/g, " ")}</Badge>}
                    {g.checkin_overdue && <Badge variant="outline" className="rounded-none text-[10px] border-amber-500/40 text-amber-700"><Clock className="w-3 h-3 mr-1" />Check-in due</Badge>}
                    {g.checkin_unresponsive && <Badge variant="outline" className="rounded-none text-[10px] border-red-500/50 text-red-700 bg-red-50/40"><AlertCircle className="w-3 h-3 mr-1" />Unresponsive</Badge>}
                  </div>
                  <div className="font-semibold text-base truncate">{g.title}</div>
                  <div className="text-[11px] text-foreground/60 mt-0.5">{g.end_date ? `due ${g.end_date}` : ""}</div>
                </div>
                <div className="w-32 shrink-0">
                  <div className="flex items-center justify-between text-xs mb-1"><span className="text-foreground/60">Progress</span><span className="font-mono font-semibold">{Math.round(g.progress_pct || 0)}%</span></div>
                  <div className="h-2 bg-foreground/10"><div className="h-full bg-primary" style={{ width: `${g.progress_pct || 0}%` }} /></div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {openGoalId && <GoalDetailDialog goalId={openGoalId} wsId={wsId} onClose={() => { setOpenGoalId(null); load(); }} />}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="rounded-none" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-9 h-9 border border-border flex items-center justify-center text-foreground/60"><Icon className="w-4 h-4" /></div>
        <div><div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div><div className="text-2xl font-bold leading-none mt-1">{value}</div></div>
      </CardContent>
    </Card>
  );
}

// ───────────────────────── Coming soon ─────────────────────────

function ComingSoon({ title, copy, testid }) {
  return (
    <Card className="rounded-none border-dashed" data-testid={`coming-soon-${testid}`}>
      <CardContent className="p-16 text-center space-y-2">
        <Clock className="w-10 h-10 mx-auto text-foreground/40" />
        <h3 className="text-lg font-bold">{title}</h3>
        <p className="text-sm text-foreground/60 max-w-md mx-auto">{copy}</p>
        <Badge variant="outline" className="rounded-none mt-2" data-testid="coming-soon-badge">Coming soon</Badge>
      </CardContent>
    </Card>
  );
}


// Feb 2026 · Compact task-count pills at the top of My Dashboard.
// "My open" uses /tasks-search?scope=assigned-me&status=open (fast).
// "Team open" uses /tasks-rollup which honours reports-tree scope; the
// tile hides itself for callers without task.view_reports_tree.
// Feb 2026 · pill deep-links + overdue drill-in — every pill is a
// <Link> to /app/tasks?scope=…&filter=… so ops jumps straight to the
// pre-scoped view. Overdue tiles carry a red tone.
function MyTaskCountPills({ wsId }) {
  const [mine, setMine] = useState({ open: null, overdue: null });
  const [team, setTeam] = useState({ open: null, overdue: null, allowed: true });
  useEffect(() => {
    if (!wsId) return;
    let alive = true;
    api.get(`/workspaces/${wsId}/tasks-search?scope=assigned-me&status=open`)
       .then((r) => {
         if (!alive) return;
         const rows = Array.isArray(r.data) ? r.data : [];
         const today = new Date(); today.setHours(0, 0, 0, 0);
         const overdue = rows.filter((t) => {
           const d = t?.due_date; if (!d) return false;
           const dt = new Date(d); if (Number.isNaN(dt.getTime())) return false;
           dt.setHours(0, 0, 0, 0);
           return dt < today;
         }).length;
         setMine({ open: rows.length, overdue });
       })
       .catch(() => { if (alive) setMine({ open: null, overdue: null }); });
    api.get(`/workspaces/${wsId}/tasks-rollup`)
       .then((r) => {
         if (!alive) return;
         const tot = r.data?.totals || r.data?.metrics || {};
         setTeam({ open: tot.active ?? 0, overdue: tot.overdue ?? 0, allowed: true });
       })
       .catch((e) => { if (alive) setTeam({ open: null, overdue: null, allowed: e?.response?.status !== 403 }); });
    return () => { alive = false; };
  }, [wsId]);
  const showTeam = team.allowed;
  return (
    <div className={`grid ${showTeam ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"} gap-2 ${showTeam ? "sm:max-w-3xl" : "sm:max-w-md"}`} data-testid="my-dashboard-task-pills">
      <Link to="/app/tasks?scope=assigned-me" className="border border-border p-3 hover:border-primary transition-colors bg-background" data-testid="my-open-tasks-pill">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">My open tasks</div>
        <div className="text-2xl font-bold mt-0.5">{mine.open == null ? "—" : mine.open}</div>
      </Link>
      <Link to="/app/tasks?scope=assigned-me&filter=overdue" className="border border-red-500/40 bg-red-500/5 p-3 hover:border-red-500 transition-colors" data-testid="my-overdue-tasks-pill">
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">My overdue</div>
        <div className="text-2xl font-bold mt-0.5 text-red-600">{mine.overdue == null ? "—" : mine.overdue}</div>
      </Link>
      {showTeam && (
        <Link to="/app/tasks?scope=all" className="border border-border p-3 hover:border-primary transition-colors bg-background" data-testid="team-open-tasks-pill">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Team open tasks</div>
          <div className="text-2xl font-bold mt-0.5">{team.open == null ? "—" : team.open}</div>
        </Link>
      )}
      {showTeam && (
        <Link to="/app/tasks?scope=all&filter=overdue" className="border border-red-500/40 bg-red-500/5 p-3 hover:border-red-500 transition-colors" data-testid="team-overdue-tasks-pill">
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Team overdue</div>
          <div className="text-2xl font-bold mt-0.5 text-red-600">{team.overdue == null ? "—" : team.overdue}</div>
        </Link>
      )}
    </div>
  );
}

